import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import type {
  CopilotInstructionProfile,
  CopilotResponseLength,
  CopilotAgent,
  CopilotUploadPolicy,
  KnowledgeScope,
} from "@yazamco/shared-types";
import { useToast } from "../../components/feedback/Toast";
import { CopilotApiError, uploadCopilotFile } from "../../lib/copilot";
import { useClickOutside } from "../../lib/useClickOutside";
import { Icon } from "../../components/ui/Icon";
import { KnowledgeScopePanel, scopeLabel } from "./KnowledgeScopePicker";
import { BusinessToolsPanel, useBusinessTools } from "./BusinessToolsPicker";
import { AgentPanel } from "./AgentPicker";
import { MicrosoftProductMark, productForKnowledgeScope } from "./MicrosoftProductMark";
import { MAX_FILES, contextFileName, type PendingFile } from "./copilot-chat";
import type { AiAgent } from "../../lib/agents";

/** Which popover is open. One at a time, as the canvas's `pop` prop is. */
type Pop = "scope" | "tools" | "files" | "answer" | "model" | null;

const LENGTHS: { id: CopilotResponseLength; label: string }[] = [
  { id: "brief", label: "Brief" },
  { id: "normal", label: "Normal" },
  { id: "detailed", label: "Detailed" },
];

interface ComposerProps {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  busy: boolean;
  onStop: () => void;

  /** Documents pinned to the next turn. */
  files: PendingFile[];
  onFilesChange: (next: (current: PendingFile[]) => PendingFile[]) => void;
  uploadPolicy: CopilotUploadPolicy;
  /** Held by the page so submitting a turn, or switching chat, can close it. */
  filePickerOpen: boolean;
  onFilePickerOpenChange: (open: boolean) => void;
  /** Raised when an upload fails for a reason a fresh sign-in would fix. */
  onReconnectRequired: (message: string) => void;

  scope: KnowledgeScope;
  onScopeChange: (scope: KnowledgeScope) => void;
  agentId: string | null;
  agents: CopilotAgent[];
  activeAgent: CopilotAgent | null;
  onAgentChange: (id: string | null) => void;

  profile: Omit<CopilotInstructionProfile, "knowledgeScope"> | null;
  onOpenSettings: () => void;
  /** Web grounding, flipped from the composer as the canvas flips it. */
  onToggleWeb: () => void;
  /** The one thing the canvas's "This answer" popover edits inline. */
  onResponseLengthChange: (length: CopilotResponseLength) => void;

  composerRef: React.RefObject<HTMLFormElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  agent?: AiAgent | null;
}

/**
 * The prompt box and everything that shapes the next question.
 *
 * Arranged exactly as `Composer.dc.html` arranges it: a bordered card whose
 * first row states the ACTIVE CONTEXT this question will run under — where it
 * searches, which business tools are on, what is attached — then the prompt,
 * then one row of controls. The four left-hand controls open popovers above
 * the card; the right-hand group is the work/web switch, the agent
 * and the single primary action, which becomes Stop while a turn streams.
 *
 * Each control is icon-only until its own popover is open, when it grows its
 * label. That is what keeps eight controls inside one 740px line without a
 * second row, and it is the canvas's behaviour, not a compression of it.
 */
export function Composer({
  draft,
  onDraftChange,
  onSubmit,
  busy,
  onStop,
  files,
  onFilesChange,
  uploadPolicy,
  filePickerOpen,
  onFilePickerOpenChange,
  onReconnectRequired,
  scope,
  onScopeChange,
  agentId,
  agents,
  activeAgent,
  onAgentChange,
  profile,
  onOpenSettings,
  onToggleWeb,
  onResponseLengthChange,
  composerRef,
  textareaRef,
  agent = null,
}: ComposerProps): JSX.Element {
  const [pop, setPop] = useState<Pop>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const tools = useBusinessTools();
  const toast = useToast();

  useClickOutside(shellRef, pop !== null, () => closePop());

  /*
   * The chat is keyboard-first: opening it puts the caret in the prompt, and
   * typing after clicking a non-interactive part of the transcript moves the
   * caret back before the browser inserts the character. Enter from that same
   * neutral surface submits an existing draft. Genuine controls and modal
   * dialogs are deliberately left alone so their keyboard behaviour remains
   * accessible.
   */
  useEffect(() => {
    const focusPrompt = (): void => {
      textareaRef.current?.focus({ preventScroll: true });
    };
    focusPrompt();

    const handleGlobalKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;

      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')) {
        return;
      }

      if (event.key.length === 1) {
        focusPrompt();
        return;
      }

      if (event.key !== "Enter") return;
      if (target?.closest('button, a, summary, [role="button"], [role="menuitem"]')) return;

      focusPrompt();
      if (draft.trim() && !busy) {
        event.preventDefault();
        composerRef.current?.requestSubmit();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, [busy, composerRef, draft, textareaRef]);

  // The attach popover's open state is owned by the page (a sent turn or a
  // chat switch closes it), so the two are kept in step rather than duplicated.
  useEffect(() => {
    if (filePickerOpen && pop !== "files") setPop("files");
    if (!filePickerOpen && pop === "files") setPop(null);
    // Only the page's flag drives this; `pop` is read, not depended on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePickerOpen]);

  useEffect(() => {
    if (agent) closePop();
    // Switching the selected agent is the only event this responds to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  function closePop(): void {
    setPop(null);
    onFilePickerOpenChange(false);
  }

  function togglePop(next: Exclude<Pop, null>): void {
    const opening = pop !== next;
    setPop(opening ? next : null);
    onFilePickerOpenChange(opening && next === "files");
  }

  // Grow with the prompt, then switch to an internal scrollbar. The canvas
  // gives the field a 62px floor and a 220px ceiling.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    const next = Math.min(Math.max(textarea.scrollHeight, 62), 220);
    textarea.style.height = `${next}px`;
    textarea.style.overflowY = textarea.scrollHeight > 220 ? "auto" : "hidden";
  }, [draft, files.length, textareaRef]);

  /*
   * Popovers used to be capped only against the viewport (`60vh`) and always
   * opened upwards. In the centred empty state the composer can sit less than
   * 420px below the body's top edge, so the body's scroll container clipped
   * the popover even though the viewport itself was tall enough. Measure the
   * actual visible room inside that container and place the popover on the
   * roomier side. Its own scrolling then handles long site/tool lists without
   * any part of the floating window disappearing behind an ancestor.
   */
  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell || !pop) return;

    const boundary = shell.closest<HTMLElement>(".cp-body");
    if (!boundary) return;

    const positionPopover = (): void => {
      const shellRect = shell.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const visibleTop = Math.max(boundaryRect.top, visualViewport?.offsetTop ?? 0);
      const visibleBottom = Math.min(
        boundaryRect.bottom,
        (visualViewport?.offsetTop ?? 0) + (visualViewport?.height ?? window.innerHeight),
      );
      const gap = 8;
      const roomAbove = Math.max(0, shellRect.top - visibleTop - gap);
      const roomBelow = Math.max(0, visibleBottom - shellRect.bottom - gap);
      const placement = roomAbove >= roomBelow ? "above" : "below";
      const availableHeight = placement === "above" ? roomAbove : roomBelow;

      shell.dataset.popPlacement = placement;
      shell.style.setProperty("--cp-pop-available-height", `${Math.floor(availableHeight)}px`);
    };

    positionPopover();

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(positionPopover);
    resizeObserver?.observe(shell);
    resizeObserver?.observe(boundary);
    boundary.addEventListener("scroll", positionPopover, { passive: true });
    window.addEventListener("resize", positionPopover);
    window.visualViewport?.addEventListener("resize", positionPopover);
    window.visualViewport?.addEventListener("scroll", positionPopover);

    return () => {
      resizeObserver?.disconnect();
      boundary.removeEventListener("scroll", positionPopover);
      window.removeEventListener("resize", positionPopover);
      window.visualViewport?.removeEventListener("resize", positionPopover);
      window.visualViewport?.removeEventListener("scroll", positionPopover);
      shell.removeAttribute("data-pop-placement");
      shell.style.removeProperty("--cp-pop-available-height");
    };
  }, [pop]);

  /** An agent may pin its knowledge source; then neither where it
   *  searches nor which tools it may use is the user's to change here. */
  const locked = Boolean(activeAgent?.knowledgeScope);
  const effectiveScope = activeAgent?.knowledgeScope ?? scope;
  const scopeProduct = productForKnowledgeScope(effectiveScope);
  const webOn = Boolean(profile?.webGrounding);

  function lockedPop(next: Exclude<Pop, null>): void {
    if (locked) {
      toast.info(
        `“${activeAgent?.name}” is locked to ${scopeLabel(effectiveScope)}. Choose another agent to change it.`,
      );
      return;
    }
    togglePop(next);
  }

  return (
    <div className="cp-composer-shell" ref={shellRef}>
      {pop === "scope" && (
        <div className="cp-pop cp-pop-scope" role="dialog" aria-label="Search in">
          <KnowledgeScopePanel value={effectiveScope} onChange={onScopeChange} onClose={closePop} />
        </div>
      )}
      {pop === "tools" && (
        <div className="cp-pop cp-pop-tools" role="dialog" aria-label="Business tools">
          <BusinessToolsPanel />
        </div>
      )}
      {pop === "files" && (
        <ComposerAttach
          files={files}
          onFilesChange={onFilesChange}
          uploadPolicy={uploadPolicy}
          onClose={closePop}
          onReconnectRequired={onReconnectRequired}
        />
      )}
      {pop === "answer" && (
        <div className="cp-pop cp-pop-answer" role="dialog" aria-label="This answer">
          <strong className="cp-pop-title">This answer</strong>
          <div className="cp-pop-answer-body">
            <div className="cp-pop-field">
              <span id="cp-length">Length</span>
              <div className="segmented" role="radiogroup" aria-labelledby="cp-length">
                {LENGTHS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    className="segmented-option"
                    aria-checked={profile?.responseLength === option.id}
                    onClick={() => onResponseLengthChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="cp-pop-settings-link"
              onClick={() => {
                closePop();
                onOpenSettings();
              }}
            >
              All Copilot settings
              <Icon name="arrowRight" size={13} stroke={2} />
            </button>
          </div>
        </div>
      )}
      {pop === "model" && (
        <div className="cp-pop cp-pop-model" role="dialog" aria-label="Agent">
          <AgentPanel
            value={agentId}
            models={agents}
            onChange={onAgentChange}
            onClose={closePop}
            onManage={onOpenSettings}
          />
        </div>
      )}

      <form ref={composerRef} className="cp-composer" onSubmit={onSubmit}>
        {/* Active context: what this question will actually use. */}
        <div className="cp-context">
          {agent ? (
            <span className="cp-context-chip cp-context-agent" title={`Chatting with ${agent.name}`}>
              <Icon name="brain" size={12} stroke={1.9} />
              <span>{agent.name}</span>
            </span>
          ) : <>
          <span className="cp-context-chip" title={`Searching ${scopeLabel(effectiveScope)}`}>
            {scopeProduct ? (
              <MicrosoftProductMark product={scopeProduct} size={14} />
            ) : (
              <Icon name="scope" size={12} stroke={1.9} />
            )}
            <span>{scopeLabel(effectiveScope)}</span>
          </span>
          {!locked &&
            tools.activeItems.map((item) => (
              <span key={item.id} className="cp-context-tool" title={item.displayName}>
                {item.icon?.startsWith("https://") ? (
                  <img src={item.icon} alt="" aria-hidden referrerPolicy="no-referrer" />
                ) : (
                  <span>{item.displayName.slice(0, 2)}</span>
                )}
              </span>
            ))}
          {files.length > 0 && (
            <span className="cp-context-files" aria-label="Attached files">
              {files.map((file) => {
                const name = file.name ?? contextFileName(file.uri);
                return (
                  <span key={file.uri} className="cp-context-chip" title={file.uri}>
                    <Icon name="file" size={12} />
                    <span>{name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() =>
                        onFilesChange((current) =>
                          current.filter((item) => item.uri !== file.uri),
                        )
                      }
                    >
                      <Icon name="close" size={10} stroke={2.4} />
                    </button>
                  </span>
                );
              })}
            </span>
          )}
          </>}
        </div>

        <label className="cp-prompt">
          <span className="visually-hidden">{agent ? `Ask ${agent.name}` : "Ask Copilot"}</span>
          <textarea
            ref={textareaRef}
            id="copilot-message"
            dir="auto"
            rows={2}
            value={draft}
            maxLength={8_000}
            placeholder={agent ? `Ask ${agent.name}…` : "Ask about a mailbox, a meeting, a customer thread or a document…"}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && pop) closePop();
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
          />
        </label>

        <div className="cp-controls">
          {!agent && <>
          <button
            type="button"
            className={`cp-control${pop === "scope" ? " is-open" : ""}${locked ? " is-locked" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={pop === "scope"}
            title={`Search in · ${scopeLabel(effectiveScope)}`}
            aria-label="Search in"
            onClick={() => lockedPop("scope")}
          >
            <Icon name="scope" size={16} />
            {pop === "scope" && <span>Search in</span>}
          </button>

          {tools.visible && (
            <button
              type="button"
              className={`cp-control${pop === "tools" ? " is-open" : ""}${locked ? " is-locked" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={pop === "tools"}
              title="Business tools"
              aria-label="Business tools"
              onClick={() => lockedPop("tools")}
            >
              <Icon name="briefcase" size={16} stroke={1.8} />
              {pop === "tools" && <span>Tools</span>}
              {tools.activeSources > 0 && (
                <span className="cp-control-count" data-on-brand aria-hidden>
                  {tools.activeSources}
                </span>
              )}
            </button>
          )}

          <button
            type="button"
            className={`cp-control${pop === "files" ? " is-open" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={pop === "files"}
            title="Attach a file"
            aria-label="Attach a file"
            disabled={busy || files.length >= MAX_FILES}
            onClick={() => togglePop("files")}
          >
            <Icon name="paperclip" size={16} />
            {pop === "files" && <span>Attach</span>}
          </button>

          <button
            type="button"
            className={`cp-control${pop === "answer" ? " is-open" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={pop === "answer"}
            title="This answer"
            aria-label="This answer"
            onClick={() => togglePop("answer")}
          >
            <Icon name="sliders" size={16} />
            {pop === "answer" && <span>This answer</span>}
          </button>
          </>}

          <span className="cp-controls-gap" />

          {!agent && <>
          <button
            type="button"
            className={`cp-control cp-control-icon${webOn ? " is-web" : ""}`}
            title={webOn ? "Work + web · tap for work only" : "Work only · tap to allow web"}
            aria-label={webOn ? "Work + web" : "Work only"}
            aria-pressed={webOn}
            onClick={onToggleWeb}
          >
            <Icon name={webOn ? "globe" : "building"} size={16} />
          </button>

          <button
            type="button"
            className={`cp-control cp-control-model${pop === "model" ? " is-open" : ""}${
              activeAgent ? " has-model" : ""
            }`}
            aria-haspopup="dialog"
            aria-expanded={pop === "model"}
            title={activeAgent?.description || activeAgent?.name || "Choose an agent"}
            aria-label={activeAgent ? `Agent: ${activeAgent.name}` : "Choose an agent"}
            onClick={() => togglePop("model")}
          >
            <Icon name="brain" size={16} />
            {activeAgent && <span className="cp-control-dot" aria-hidden />}
            {pop === "model" && <span>{activeAgent?.name ?? "Agent"}</span>}
          </button>
          </>}

          {busy ? (
            <button
              className="cp-stop"
              type="button"
              onClick={onStop}
              aria-label="Stop"
              title="Stop"
            >
              <Icon name="stop" size={14} />
            </button>
          ) : (
            <button
              className="cp-send"
              type="submit"
              data-testid="composer-send"
              disabled={!draft.trim()}
              aria-label={agent ? `Ask ${agent.name}` : "Ask Copilot"}
              title={agent ? `Ask ${agent.name}` : "Ask Copilot"}
            >
              <Icon name="arrowRight" size={17} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/**
 * Error codes a reconnect can actually fix — the same set the page uses. A
 * genuine `COPILOT_ACCESS_DENIED` is deliberately absent.
 */
const RECONNECTABLE = new Set(["COPILOT_AUTH_REQUIRED", "COPILOT_CONSENT_REQUIRED"]);

/**
 * The attach popover: the canvas's dashed drop target over a "browse" link,
 * and below it the way to name a file that is already in OneDrive or
 * SharePoint.
 *
 * A file from this computer goes to the user's own OneDrive first, because
 * what a turn can carry is a URI. Failures are reported on the popover rather
 * than in the transcript — nothing has been asked yet, and an error in the
 * chat history would outlive the attempt.
 */
function ComposerAttach({
  files,
  onFilesChange,
  uploadPolicy,
  onClose,
  onReconnectRequired,
}: {
  files: PendingFile[];
  onFilesChange: (next: (current: PendingFile[]) => PendingFile[]) => void;
  uploadPolicy: CopilotUploadPolicy;
  onClose: () => void;
  onReconnectRequired: (message: string) => void;
}): JSX.Element {
  const toast = useToast();
  const [fileUrl, setFileUrl] = useState("");
  const [fileUrlError, setFileUrlError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function uploadLocalFile(file: File): Promise<void> {
    if (files.length >= MAX_FILES) {
      setFileUrlError(`You can attach up to ${MAX_FILES} files.`);
      return;
    }
    if (uploadPolicy.maxBytes > 0 && file.size > uploadPolicy.maxBytes) {
      setFileUrlError(
        `That file is larger than ${Math.floor(uploadPolicy.maxBytes / 1_000_000)} MB. Paste a link to it instead.`,
      );
      return;
    }
    setUploading(true);
    setFileUrlError(null);
    try {
      const uploaded = await uploadCopilotFile(file);
      // Re-checked after the await: the ceiling could have been reached while
      // the upload was in flight, and the file is in OneDrive either way.
      onFilesChange((current) =>
        current.length >= MAX_FILES || current.some((item) => item.uri === uploaded.uri)
          ? current
          : [...current, { uri: uploaded.uri, name: uploaded.name }],
      );
      onClose();
      // Saved in the person's own OneDrive, and left there — said plainly,
      // because a file appearing in their drive should not be a surprise.
      toast.success(`${uploaded.name} was saved to your OneDrive and attached.`);
    } catch (error) {
      if (error instanceof CopilotApiError && RECONNECTABLE.has(error.code)) {
        onReconnectRequired(error.message);
        onClose();
      }
      setFileUrlError(
        error instanceof Error ? error.message : "The file could not be uploaded.",
      );
    } finally {
      setUploading(false);
      // Cleared so choosing the SAME file again still fires `change`.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function attachLink(): void {
    const uri = fileUrl.trim();
    let parsed: URL;
    try {
      parsed = new URL(uri);
    } catch {
      setFileUrlError("Paste a valid SharePoint or OneDrive link.");
      return;
    }
    if (parsed.protocol !== "https:") {
      setFileUrlError("The file link must use HTTPS.");
      return;
    }
    if (files.some((file) => file.uri === uri)) {
      setFileUrlError("This file is already attached.");
      return;
    }
    if (files.length >= MAX_FILES) {
      setFileUrlError(`You can attach up to ${MAX_FILES} files.`);
      return;
    }
    onFilesChange((current) => [...current, { uri }]);
    setFileUrl("");
    setFileUrlError(null);
    onClose();
  }

  const maxMb = uploadPolicy.maxBytes > 0 ? Math.floor(uploadPolicy.maxBytes / 1_000_000) : 20;

  return (
    <div className="cp-pop cp-pop-files" role="dialog" aria-label="Attach a file">
      <strong className="cp-pop-title">Attach a file</strong>

      {uploadPolicy.enabled && (
        <button
          type="button"
          className={`cp-drop${dragging ? " is-dragging" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            const dropped = event.dataTransfer.files?.[0];
            if (dropped) void uploadLocalFile(dropped);
          }}
        >
          <Icon name="upload" size={18} />
          <span>
            {uploading ? "Uploading…" : <>Drop a file, or <b>browse</b></>}
          </span>
          <small>PDF, Word, Excel, PowerPoint or text · up to {maxMb} MB</small>
          {/* Kept out of the label flow and reset after every pick, so
              choosing the same file twice still fires `change`. */}
          <input
            ref={fileInputRef}
            type="file"
            className="visually-hidden"
            tabIndex={-1}
            onChange={(event) => {
              const chosen = event.target.files?.[0];
              if (chosen) void uploadLocalFile(chosen);
            }}
          />
        </button>
      )}

      <div className="cp-pop-field cp-attach-link">
        <span id="cp-attach-url">Pick from OneDrive or SharePoint</span>
        <input
          type="url"
          value={fileUrl}
          aria-labelledby="cp-attach-url"
          placeholder="https://…/document.docx"
          onChange={(event) => {
            setFileUrl(event.target.value);
            setFileUrlError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              attachLink();
            }
          }}
        />
      </div>

      {fileUrlError && (
        <p className="cp-pop-error" role="alert">
          {fileUrlError}
        </p>
      )}

      <button
        type="button"
        className="cp-attach-submit"
        disabled={!fileUrl.trim() || uploading}
        onClick={attachLink}
      >
        <Icon name="folderFlat" size={14} />
        Attach this link
      </button>
    </div>
  );
}
